/**
 * FlowBuilder — full-page admin editor for one automation flow
 * (/crm/settings/flows/:id).
 *
 * The flow's working copy lives in crm.automation_flows.definition (draft);
 * Publish snapshots it into an immutable automation_flow_versions row that
 * in-flight enrollments pin to (THE RUNNER LAW — a published version never
 * changes under a walking enrollment). Node ids are minted ONCE when a step
 * is added and never change; `entry` is always written explicitly.
 *
 * The server's §6.10 publish validation is authoritative; `flowIssues` below
 * mirrors it for inline feedback (including the DAG check — flows run each
 * step AT MOST ONCE per enrollment, so "ask again" means duplicating a step,
 * never looping back).
 *
 * Layout: meta header (name/status/save/publish) → trigger + enrollment-rules
 * card → step list (each card expands into a per-type editor) with a flow-map
 * card alongside on desktop showing the path from Start and any issues.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight,
  Loader2, Plus, Trash2, UploadCloud,
} from 'lucide-react';
import api from '../../../services/api';
import { CrmFlow, FlowConditionDef, FlowDefinition, FlowDuration, FlowNodeDef, FlowVersionSummary } from '../../types';
import { useIsCRMAdmin, useTeam } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

// ---- vocabulary (mirrors backend crmFlows.js) -------------------------------

const NODE_TYPE_META: { value: string; label: string; blurb: string }[] = [
  { value: 'send_sms', label: 'Send a text', blurb: 'Texts the patient (respects opt-out and quiet hours).' },
  { value: 'send_options', label: 'Send the 1/2/3 options menu', blurb: 'Booking link, form link, or "not interested" — needs a published comprehensive form.' },
  { value: 'send_email', label: 'Send an email', blurb: 'Emails the patient (respects opt-out and bounces).' },
  { value: 'wait', label: 'Wait', blurb: 'Pause for a set time, or until business hours.' },
  { value: 'wait_for_reply', label: 'Wait for a reply', blurb: 'Pause until the patient texts back, or a timeout passes.' },
  { value: 'branch', label: 'Branch', blurb: 'Route by the reply or a contact field.' },
  { value: 'create_task', label: 'Create a task', blurb: 'Adds an unassigned task to the team worklist.' },
  { value: 'update_contact', label: 'Update the contact', blurb: 'Set pipeline status, eligibility, or owner.' },
  { value: 'exit', label: 'End the flow', blurb: 'Stops here.' },
];
const typeMeta = (t: string) => NODE_TYPE_META.find((m) => m.value === t);

const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const BRANCH_OPS = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'exists', label: 'has any value' },
];
const LIFECYCLES = ['new', 'contacting', 'qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened', 'randomized', 'screen_failed', 'not_eligible', 'not_interested', 'unreachable'];
const ELIGIBILITIES = ['eligible', 'not_eligible', 'needs_review'];
const INTENTS = [
  { value: 'book_call', label: 'book a call (replied 1)' },
  { value: 'fill_form', label: 'online questions (replied 2)' },
  { value: 'not_interested', label: 'not interested (replied 3)' },
];
const BRANCH_FIELD_PRESETS = [
  { value: 'context.last_reply.intent', label: 'Reply intent (1/2/3 menu)' },
  { value: 'contact.lifecycle_status', label: 'Pipeline status' },
  { value: 'contact.sms_opt_out', label: 'Opted out of SMS' },
  { value: 'contact.email', label: 'Email address' },
];
// Mirrors BRANCH_CONTACT_FIELDS in backend crmFlows.js — the only contact
// columns the runner loads; anything else is undefined at run time.
const BRANCH_CONTACT_FIELDS = [
  'id', 'first_name', 'last_name', 'full_name', 'phone_e164', 'email',
  'sms_opt_out', 'lifecycle_status', 'merged_into_id',
];
const MERGE_FIELDS_HINT = 'Merge fields: {{first_name}} {{last_name}} {{full_name}} {{study_title}}';

// ---- small helpers ----------------------------------------------------------

interface NodeEntry { id: string; node: FlowNodeDef; }

function mintNodeId(type: string, existing: Set<string>): string {
  const base = type.replace(/_/g, '-');
  for (let n = 1; ; n++) {
    const id = `${base}-${n}`;
    if (!existing.has(id)) return id;
  }
}

/** Human one-liner for a node — used by edge selects and the flow map. */
function nodeLabel(entryIdx: number, node: FlowNodeDef): string {
  const t = typeMeta(node.type)?.label || node.type;
  let snippet = '';
  if (node.type === 'send_sms' && node.body) snippet = node.body;
  if (node.type === 'send_email' && node.subject) snippet = node.subject;
  if (node.type === 'create_task' && node.title) snippet = node.title;
  if (node.type === 'update_contact' && node.set?.lifecycle_status) snippet = `→ ${node.set.lifecycle_status}`;
  if (snippet.length > 32) snippet = `${snippet.slice(0, 32)}…`;
  return `${entryIdx + 1}. ${t}${snippet ? ` — ${snippet}` : ''}`;
}

/** Duration <-> day/hour/minute/second inputs — LOSSLESS against the backend
 *  schema (a number is raw seconds; the object form may carry `seconds`).
 *  Dropping a component here would falsely block Publish on a server-valid
 *  flow and silently rewrite the wait on the next edit. */
interface DurationParts { days: string; hours: string; minutes: string; seconds: string; }
function durationToParts(d: FlowDuration | undefined): DurationParts {
  if (typeof d === 'number') {
    const days = Math.floor(d / 86400);
    const hours = Math.floor((d % 86400) / 3600);
    const minutes = Math.floor((d % 3600) / 60);
    const seconds = Math.floor(d % 60);
    return {
      days: days ? String(days) : '',
      hours: hours ? String(hours) : '',
      minutes: minutes ? String(minutes) : '',
      seconds: seconds ? String(seconds) : '',
    };
  }
  if (d && typeof d === 'object') {
    return {
      days: d.days ? String(d.days) : '',
      hours: d.hours ? String(d.hours) : '',
      minutes: d.minutes ? String(d.minutes) : '',
      seconds: d.seconds ? String(d.seconds) : '',
    };
  }
  return { days: '', hours: '', minutes: '', seconds: '' };
}
function partsToDuration(p: DurationParts): FlowDuration | undefined {
  const out: { days?: number; hours?: number; minutes?: number; seconds?: number } = {};
  for (const k of ['days', 'hours', 'minutes', 'seconds'] as const) {
    const n = Number(p[k]);
    if (p[k] !== '' && Number.isFinite(n) && n > 0) out[k] = n;
  }
  return Object.keys(out).length ? out : undefined;
}
function durationLabel(d: FlowDuration | undefined): string {
  const p = durationToParts(d);
  const bits = [
    p.days && `${p.days}d`, p.hours && `${p.hours}h`,
    p.minutes && `${p.minutes}m`, p.seconds && `${p.seconds}s`,
  ].filter(Boolean);
  return bits.length ? bits.join(' ') : 'no time set';
}

/**
 * Client mirror of the server's §6.10 publish validation (crmFlows.js is
 * authoritative — this exists for inline feedback only). Returns human issue
 * strings; empty = publishable.
 */
function flowIssues(entry: string | null, nodes: NodeEntry[]): string[] {
  const issues: string[] = [];
  const byId = new Map(nodes.map((n) => [n.id, n.node]));
  const name = (id: string) => {
    const idx = nodes.findIndex((n) => n.id === id);
    return idx >= 0 ? nodeLabel(idx, nodes[idx].node) : id;
  };
  if (nodes.length === 0) { issues.push('Add at least one step.'); return issues; }
  if (!entry || !byId.has(entry)) issues.push('Pick a starting step.');

  const checkEdge = (v: string | null | undefined, where: string) => {
    if (typeof v === 'string' && v && !byId.has(v)) issues.push(`${where} points at a deleted step.`);
  };

  nodes.forEach((n, i) => {
    const node = n.node;
    const label = nodeLabel(i, node);
    checkEdge(node.next, label);
    if (node.type === 'send_sms' && !(node.body || '').trim()) issues.push(`${label}: needs a message.`);
    if (node.type === 'send_email') {
      if (!(node.subject || '').trim()) issues.push(`${label}: needs a subject.`);
      if (!(node.body || '').trim()) issues.push(`${label}: needs a body.`);
    }
    if (node.type === 'wait' && !node.until_business_hours && !partsToDuration(durationToParts(node.duration))) {
      issues.push(`${label}: needs a wait time (or "until business hours").`);
    }
    if (node.type === 'wait_for_reply') {
      if (!partsToDuration(durationToParts(node.timeout))) issues.push(`${label}: needs a reply window.`);
      if (typeof node.on_timeout !== 'string' || !node.on_timeout) {
        issues.push(`${label}: pick where to go if the patient never replies.`);
      } else checkEdge(node.on_timeout, label);
    }
    if (node.type === 'branch') {
      const conds = node.conditions || [];
      if (conds.length === 0) issues.push(`${label}: add at least one condition.`);
      conds.forEach((c, ci) => {
        // Trim BEFORE validating — the server does (a pasted edge space is
        // healed at save, and the mirror must not block what the server accepts).
        const field = (c.when?.field || '').trim();
        if (!/^(contact|context)\.\S+$/.test(field)) issues.push(`${label}: condition ${ci + 1} needs a field (contact.… or context.…, no spaces).`);
        else if (field.startsWith('contact.') && !BRANCH_CONTACT_FIELDS.includes(field.slice(8))) {
          issues.push(`${label}: condition ${ci + 1} — "${field}" isn't a contact field flows can read.`);
        }
        const op = c.when?.op || 'eq';
        const v = (c.when || {} as any).value;
        if (op !== 'exists' && (!('value' in (c.when || {})) || (typeof v === 'string' && !v.trim()))) {
          issues.push(`${label}: condition ${ci + 1} needs a value.`);
        }
        if (typeof c.goto !== 'string' || !c.goto) issues.push(`${label}: condition ${ci + 1} needs a "go to" step.`);
        else checkEdge(c.goto, label);
      });
      if (typeof node.else !== 'string' || !node.else) issues.push(`${label}: pick an "otherwise" step.`);
      else checkEdge(node.else, label);
    }
    if (node.type === 'create_task' && node.priority && !TASK_PRIORITIES.includes(node.priority)) {
      issues.push(`${label}: bad priority.`);
    }
    if (node.type === 'update_contact' && Object.keys(node.set || {}).length === 0) {
      issues.push(`${label}: pick at least one field to update.`);
    }
  });

  // DAG check — same three-color DFS as the server. Steps run at most once
  // per enrollment, so a loop would silently dead-end on its second visit.
  const outEdges = (node: FlowNodeDef): string[] => {
    const edges: string[] = [];
    if (typeof node.next === 'string' && node.next) edges.push(node.next);
    if (typeof node.on_timeout === 'string' && node.on_timeout) edges.push(node.on_timeout);
    if (typeof node.else === 'string' && node.else) edges.push(node.else);
    for (const c of node.conditions || []) if (typeof c.goto === 'string' && c.goto) edges.push(c.goto);
    return edges.filter((e) => byId.has(e));
  };
  const color = new Map<string, number>();
  for (const start of nodes) {
    if (color.get(start.id)) continue;
    const stack: [string, number][] = [[start.id, 0]];
    color.set(start.id, 1);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const edges = outEdges(byId.get(top[0])!);
      if (top[1] < edges.length) {
        const next = edges[top[1]++];
        if (color.get(next) === 1) {
          issues.push(`Loop through "${name(next)}" — steps run once each; duplicate the step instead of looping back.`);
          return issues;
        }
        if (!color.get(next)) { color.set(next, 1); stack.push([next, 0]); }
      } else {
        color.set(top[0], 2);
        stack.pop();
      }
    }
  }
  return issues;
}

/** Reachable node ids from entry, in BFS order (for the flow map). */
function reachableFrom(entry: string | null, nodes: NodeEntry[]): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n.node]));
  if (!entry || !byId.has(entry)) return [];
  const seen = new Set<string>([entry]);
  const order = [entry];
  for (let i = 0; i < order.length; i++) {
    const node = byId.get(order[i])!;
    const edges: (string | null | undefined)[] = [
      node.next, node.on_timeout, node.else,
      ...(node.conditions || []).map((c) => c.goto),
    ];
    for (const e of edges) {
      if (typeof e === 'string' && byId.has(e) && !seen.has(e)) {
        seen.add(e);
        order.push(e);
      }
    }
  }
  return order;
}

// ---- shared field bits -------------------------------------------------------

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

function EdgeSelect({
  value, onChange, nodes, exceptId, label, endLabel = 'End the flow',
}: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  nodes: NodeEntry[];
  /** Hide this node id from the options (no self-edges from the builder). */
  exceptId?: string;
  label: string;
  endLabel?: string;
}) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={`${inputCls} min-h-[44px] sm:min-h-0`}
      >
        <option value="">— {endLabel} —</option>
        {nodes.filter((n) => n.id !== exceptId).map((n) => {
          // The index shown must match the card numbering — recompute against
          // the FULL list, not the filtered one.
          const fullIdx = nodes.findIndex((x) => x.id === n.id);
          return <option key={n.id} value={n.id}>{nodeLabel(fullIdx, n.node)}</option>;
        })}
      </select>
    </div>
  );
}

function DurationFields({
  value, onChange,
}: { value: FlowDuration | undefined; onChange: (d: FlowDuration | undefined) => void }) {
  const parts = durationToParts(value);
  const set = (k: keyof DurationParts, v: string) => {
    onChange(partsToDuration({ ...parts, [k]: v }));
  };
  // The seconds box only shows when the duration already carries seconds
  // (API-authored) — recruiter-built waits are day/hour/minute scale.
  const keys = (parts.seconds ? ['days', 'hours', 'minutes', 'seconds'] : ['days', 'hours', 'minutes']) as (keyof DurationParts)[];
  return (
    <div className="flex flex-wrap items-end gap-2">
      {keys.map((k) => (
        <div key={k} className="w-20">
          <span className={labelCls}>{k}</span>
          <input
            type="number"
            min={0}
            max={366}
            value={parts[k]}
            onChange={(e) => set(k, e.target.value)}
            className={`${inputCls} min-h-[44px] sm:min-h-0`}
          />
        </div>
      ))}
    </div>
  );
}

// ---- the page -----------------------------------------------------------------

export default function FlowBuilder() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // When opened from a study's Flows tab the study id rides along so we can
  // offer a back-link to it (validated — it goes straight into a route path).
  const studyParam = searchParams.get('study');
  const studyId = studyParam && /^[0-9a-f-]{36}$/i.test(studyParam) ? studyParam : null;
  const isAdmin = useIsCRMAdmin();
  const { team } = useTeam();

  const [flow, setFlow] = useState<CrmFlow | null>(null);
  const [versions, setVersions] = useState<FlowVersionSummary[]>([]);
  const [meta, setMeta] = useState({ name: '', description: '' });
  // Status is deliberately NOT part of the draft form — enrollment matching
  // reads it live, so flipping it is its own immediate, confirmed action.
  const [status, setStatus] = useState('draft');
  const [statusSaving, setStatusSaving] = useState(false);
  // Trigger filters as comma-separated text (parsed to arrays on save).
  // extraTrigger preserves filter keys this UI doesn't render (e.g. an
  // API-authored questionnaire_id list) — a wholesale PATCH must never
  // silently widen a live flow's enrollment scope.
  const [trigger, setTrigger] = useState({ study_slug: '', questionnaire_id: '', eligibility_status: '', utm_source: '' });
  const [extraTrigger, setExtraTrigger] = useState<Record<string, any>>({});
  const [rules, setRules] = useState({ reenroll: false, dedupe: '' });
  const [nodes, setNodes] = useState<NodeEntry[]>([]);
  const [entry, setEntry] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  // Ref mirror of `dirty` for async readers (the silent post-publish reload
  // must never clobber edits typed while the publish pipeline was in flight),
  // plus a monotonically increasing edit sequence: saveDraft may only clear
  // the dirty flag if NO edit landed after it captured its payload —
  // otherwise keystrokes typed during the PATCH round-trip would be marked
  // "saved" without ever reaching the server.
  const dirtyRef = useRef(false);
  const editSeqRef = useRef(0);
  const markDirty = (v: boolean) => { dirtyRef.current = v; setDirty(v); };
  // The latest published snapshot's definition (server-normalized jsonb, like
  // the stored draft — so string comparison is exact). Lets the activate path
  // say truthfully whether unpublished step edits exist.
  const [latestDefinition, setLatestDefinition] = useState<FlowDefinition | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const triggerToText = (v: string | string[] | undefined): string =>
    Array.isArray(v) ? v.join(', ') : (v || '');

  const load = useCallback(async (silent = false) => {
    // §5b.5: page spinner on FIRST load only; refreshes (e.g. after publish)
    // are silent so scroll position and rendered content survive.
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/api/crm/flows/${id}`);
      const f: CrmFlow = res.data.flow;
      setFlow(f);
      setVersions(res.data.versions || []);
      setLatestDefinition(res.data.latest_definition ?? null);
      setStatus(f.status);
      // A silent reload must never clobber FORM state typed while it was in
      // flight (publish is a 3-request pipeline and the form stays usable) —
      // server-truth metadata (flow row, versions, status) refreshed above.
      if (silent && dirtyRef.current) return;
      setMeta({ name: f.name, description: f.description || '' });
      const cfg = f.trigger_config || {};
      setTrigger({
        study_slug: triggerToText(cfg.study_slug),
        questionnaire_id: triggerToText(cfg.questionnaire_id),
        eligibility_status: triggerToText(cfg.eligibility_status),
        utm_source: triggerToText(cfg.utm_source),
      });
      setExtraTrigger(Object.fromEntries(Object.entries(cfg).filter(
        ([k]) => !['study_slug', 'questionnaire_id', 'eligibility_status', 'utm_source'].includes(k)
      )));
      setRules({
        reenroll: f.enrollment_rules?.reenroll === true,
        dedupe: f.enrollment_rules?.dedupe_window_hours ? String(f.enrollment_rules.dedupe_window_hours) : '',
      });
      const def = f.definition && f.definition.nodes && typeof f.definition.nodes === 'object' ? f.definition : { entry: null, nodes: {} };
      // jsonb does NOT preserve key order — display steps in FLOW order
      // (walk from the entry), with anything unreachable appended after.
      const all = Object.entries(def.nodes).map(([nid, node]) => ({ id: nid, node: node as FlowNodeDef }));
      const defEntry = typeof def.entry === 'string' && def.entry && def.nodes[def.entry] ? def.entry : (all[0]?.id ?? null);
      const flowOrder = reachableFrom(defEntry, all);
      const rank = new Map(flowOrder.map((nid, i) => [nid, i]));
      all.sort((a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9));
      setNodes(all);
      setEntry(defEntry);
      // The re-sort can change which step lives at a given index — never let
      // an expanded card silently become a different step's editor.
      setOpenIdx(null);
      markDirty(false);
    } catch (e: any) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const touch = () => { editSeqRef.current += 1; markDirty(true); setNotice(null); };

  const setNode = (i: number, node: FlowNodeDef) => {
    setNodes((list) => list.map((n, j) => (j === i ? { ...n, node } : n)));
    touch();
  };

  const addNode = (type: string) => {
    const existing = new Set(nodes.map((n) => n.id));
    const nid = mintNodeId(type, existing);
    const node: FlowNodeDef = { type };
    if (type === 'wait_for_reply') node.timeout = { days: 2 };
    if (type === 'branch') node.conditions = [{ when: { field: 'context.last_reply.intent', op: 'eq', value: 'book_call' }, goto: null }];
    if (type === 'create_task') { node.title = ''; node.priority = 'normal'; }
    if (type === 'update_contact') node.set = {};
    const next = [...nodes, { id: nid, node }];
    setNodes(next);
    if (!entry) setEntry(next[0].id);
    setOpenIdx(next.length - 1);
    setAddOpen(false);
    touch();
  };

  const remove = (i: number) => {
    const removedId = nodes[i].id;
    // Null out every edge that pointed at the removed step so the draft stays
    // coherent (the issues panel would otherwise show phantom "deleted step"
    // errors the user can't see how to fix).
    const rest = nodes
      .filter((_, j) => j !== i)
      .map((n) => {
        const node = { ...n.node };
        if (node.next === removedId) node.next = null;
        if (node.on_timeout === removedId) node.on_timeout = null;
        if (node.else === removedId) node.else = null;
        if (node.conditions?.some((c) => c.goto === removedId)) {
          node.conditions = node.conditions.map((c) => (c.goto === removedId ? { ...c, goto: null } : c));
        }
        return { ...n, node };
      });
    setNodes(rest);
    if (entry === removedId) setEntry(rest.length ? rest[0].id : null);
    setOpenIdx(null);
    touch();
  };

  const buildDefinition = (): FlowDefinition => ({
    entry: entry && nodes.some((n) => n.id === entry) ? entry : (nodes[0]?.id ?? null),
    nodes: Object.fromEntries(nodes.map((n) => [n.id, n.node])),
  });

  const parseList = (text: string): string[] =>
    text.split(',').map((s) => s.trim()).filter(Boolean);

  const buildTriggerConfig = (): Record<string, any> => {
    // Spread the keys this UI doesn't render FIRST — a save must never strip
    // an API-authored filter and silently widen who enrolls.
    const out: Record<string, any> = { ...extraTrigger };
    for (const k of ['study_slug', 'questionnaire_id', 'eligibility_status', 'utm_source'] as const) {
      const vals = parseList(trigger[k]);
      if (vals.length) out[k] = vals;
    }
    return out;
  };

  const buildRules = () => {
    const out: { reenroll?: boolean; dedupe_window_hours?: number } = {};
    if (rules.reenroll) {
      out.reenroll = true;
      const n = Number(rules.dedupe);
      if (rules.dedupe !== '' && Number.isFinite(n) && n > 0) out.dedupe_window_hours = n;
    }
    return out;
  };

  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (saving) return false;
    setSaving(true);
    setError(null);
    setNotice(null);
    // Edits landing AFTER this point are not in the payload — the success
    // handler must not mark them saved.
    const seqAtCapture = editSeqRef.current;
    try {
      const res = await api.patch(`/api/crm/flows/${id}`, {
        name: meta.name,
        description: meta.description || null,
        trigger_config: buildTriggerConfig(),
        enrollment_rules: buildRules(),
        definition: buildDefinition(),
      });
      setFlow(res.data.flow);
      if (editSeqRef.current === seqAtCapture) markDirty(false);
      setNotice(status === 'active'
        ? 'Saved. Step changes wait for the next Publish; filter and re-enrollment changes are live now.'
        : (flow && flow.version > 0
          ? `Draft saved. Patients get published v${flow.version} — Publish to apply step changes.`
          : 'Draft saved.'));
      return true;
    } catch (e: any) {
      setError(errMsg(e));
      return false;
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, id, flow, meta, status, trigger, extraTrigger, rules, nodes, entry]);

  // "The draft has edits the published snapshot lacks" — compared by CONTENT
  // (both sides are server-normalized jsonb, so string equality is exact).
  // Timestamp heuristics are useless here: publish itself bumps the flow row's
  // updated_at AFTER the snapshot row, so they'd flag every published flow.
  const draftNewerThanPublish = Boolean(
    flow && flow.version > 0 && latestDefinition
    && JSON.stringify(flow.definition) !== JSON.stringify(latestDefinition)
  );

  // Status is live the moment it lands (enrollment matching reads the flow
  // row), so it gets its own immediate PATCH — never riding "Save draft" —
  // and activating asks first: from then on every new matching lead enrolls.
  const changeStatus = async (next: string) => {
    if (statusSaving || next === status) return;
    if (next === 'active') {
      // Say WHICH copy goes live: enrollments pin the published snapshot, so
      // unpublished step edits (a just-saved draft) will NOT be what patients
      // receive until the next Publish. A never-published flow enrolls NOBODY
      // — don't assert messaging that won't happen.
      const ok = window.confirm(
        (flow?.version || 0) < 1
          ? "This flow has never been published — activating it won't enroll anyone until you Publish. Activate anyway?"
          : `Activate this flow? Every new matching lead will be enrolled and messaged with published v${flow!.version}.`
            + ((dirty || draftNewerThanPublish) ? ' Your unpublished step edits will NOT apply until you Publish.' : '')
      );
      if (!ok) return;
    } else if (status === 'active') {
      // Honest about what "off" means: only NEW enrollments stop — patients
      // already walking the flow finish their remaining steps.
      const ok = window.confirm(
        'Stop enrolling new leads in this flow? Patients already in it will still finish their remaining steps.'
      );
      if (!ok) return;
    }
    const prev = status;
    setStatus(next);
    setStatusSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/api/crm/flows/${id}`, { status: next });
      setFlow(res.data.flow);
      setNotice(next === 'active'
        ? ((res.data.flow?.version ?? 0) < 1
          ? 'Flow is active, but never published — nobody enrolls until you Publish.'
          : `Flow is now active — new matching leads get published v${res.data.flow.version}.`)
        : `Flow is now ${next} — new leads won't enroll; patients already in it finish their steps.`);
    } catch (e: any) {
      setStatus(prev);
      setError(errMsg(e));
    } finally {
      setStatusSaving(false);
    }
  };

  const publish = async () => {
    if (publishing) return;
    // Publishing a DRAFT flow also activates it (the forms pattern) — that is
    // the most common activation path, so it carries the same explicit
    // confirm as the status dropdown.
    if (status === 'draft') {
      const ok = window.confirm(
        'Publish and activate this flow? Every new matching lead will be enrolled and messaged by it.'
      );
      if (!ok) return;
    }
    // Publish snapshots the SERVER's draft — flush local edits first.
    if (dirty) {
      const ok = await saveDraft();
      if (!ok) return;
    }
    setPublishing(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/flows/${id}/publish`);
      const v = res.data?.version?.version;
      setNotice(status === 'archived'
        ? `Published v${v} — but the flow is archived, so nobody will enroll until it's set to Active.`
        : `Published v${v}. New enrollments use it${status === 'draft' ? ' — the flow is now active' : ''}; anyone already in the flow finishes on their version.`);
      await load(true);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  // The same effective entry buildDefinition will save — the issues panel
  // must judge what would actually publish, not a stale-but-unset entry.
  const effectiveEntry = entry && nodes.some((n) => n.id === entry) ? entry : (nodes[0]?.id ?? null);
  const issues = useMemo(() => flowIssues(effectiveEntry, nodes), [effectiveEntry, nodes]);
  const reachable = useMemo(() => reachableFrom(effectiveEntry, nodes), [effectiveEntry, nodes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-primary-600" />
      </div>
    );
  }
  if (loadError || !flow) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {loadError || 'Flow not found'}
        </div>
        <div className="mt-3 flex items-center justify-center gap-4">
          <button
            onClick={() => load()}
            className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
          >
            Retry
          </button>
          <Link
            to={studyId ? `/crm/settings/studies/${studyId}?tab=flows` : '/crm/settings/flows'}
            className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400"
          >
            {studyId ? 'Back to study' : 'Back to flows'}
          </Link>
        </div>
      </div>
    );
  }

  const idxOf = (nid: string) => nodes.findIndex((n) => n.id === nid);

  // ---- per-type editors (controlled by the lifted nodes state) --------------

  const renderEditor = (i: number, node: FlowNodeDef) => {
    const patch = (p: Partial<FlowNodeDef>) => setNode(i, { ...node, ...p });

    if (node.type === 'send_sms' || node.type === 'send_email') {
      return (
        <div className="space-y-3">
          {node.type === 'send_email' && (
            <div>
              <span className={labelCls}>Subject</span>
              <input
                type="text"
                value={node.subject || ''}
                onChange={(e) => patch({ subject: e.target.value })}
                maxLength={200}
                className={`${inputCls} min-h-[44px] sm:min-h-0`}
              />
            </div>
          )}
          <div>
            <span className={labelCls}>{node.type === 'send_sms' ? 'Message' : 'Body'}</span>
            <textarea
              rows={3}
              value={node.body || ''}
              onChange={(e) => patch({ body: e.target.value })}
              maxLength={node.type === 'send_sms' ? 1600 : 10000}
              className={inputCls}
            />
            <p className="mt-1 text-[11px] text-gray-400">{MERGE_FIELDS_HINT}</p>
          </div>
          {node.type === 'send_sms' && (
            <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
              <input
                type="checkbox"
                checked={Boolean(node.quiet_hours_exempt)}
                onChange={(e) => patch({ quiet_hours_exempt: e.target.checked || undefined })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Send even during quiet hours (only for replies the patient asked for)
            </label>
          )}
          <EdgeSelect label="Then" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
        </div>
      );
    }

    if (node.type === 'send_options') {
      return (
        <div className="space-y-3">
          <p className="rounded-lg bg-primary-50 p-2.5 text-xs text-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            Sends the standard "reply 1 to book a call, 2 for the online questions, 3 if not interested"
            text with a booking link and a form link. Needs a published <strong>comprehensive</strong> form
            (Forms in the sidebar) — without one, enrollments stop here with an error.
          </p>
          <EdgeSelect label="Then (usually a Wait-for-reply step)" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
        </div>
      );
    }

    if (node.type === 'wait') {
      const businessHours = node.until_business_hours === true;
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
              <input
                type="radio"
                checked={!businessHours}
                // timezone is deliberately untouched — this UI doesn't render
                // it, and a radio toggle must never destroy an API-authored
                // field it doesn't own.
                onChange={() => patch({ until_business_hours: undefined })}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Wait a set time
            </label>
            <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
              <input
                type="radio"
                checked={businessHours}
                onChange={() => patch({ until_business_hours: true, duration: undefined })}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Until business hours (8am–9pm — continues immediately if already within them)
            </label>
          </div>
          {!businessHours && <DurationFields value={node.duration} onChange={(d) => patch({ duration: d })} />}
          <EdgeSelect label="Then" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
        </div>
      );
    }

    if (node.type === 'wait_for_reply') {
      return (
        <div className="space-y-3">
          <div>
            <span className={labelCls}>Give them this long to reply</span>
            <DurationFields value={node.timeout} onChange={(d) => patch({ timeout: d })} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <EdgeSelect label="If they reply" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
            <EdgeSelect label="If they stay silent" value={node.on_timeout} onChange={(v) => patch({ on_timeout: v })} nodes={nodes} exceptId={nodes[i].id} endLabel="(required — pick a step)" />
          </div>
          <p className="text-[11px] text-gray-400">
            Tip: a flow can't loop back to the same wait step — to ask again, add a second wait step (see the starter flow).
          </p>
        </div>
      );
    }

    if (node.type === 'branch') {
      const conds = node.conditions || [];
      const setCond = (ci: number, c: FlowConditionDef) => {
        patch({ conditions: conds.map((x, j) => (j === ci ? c : x)) });
      };
      return (
        <div className="space-y-3">
          {conds.map((c, ci) => {
            const field = c.when?.field || '';
            const op = c.when?.op || 'eq';
            const isPreset = BRANCH_FIELD_PRESETS.some((p) => p.value === field);
            return (
              <div key={ci} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{ci === 0 ? 'If' : 'Else if'}</span>
                  <button
                    onClick={() => patch({ conditions: conds.filter((_, j) => j !== ci) })}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-1 text-gray-400 hover:text-red-600 sm:min-h-0 sm:min-w-0"
                    aria-label="Remove condition"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <span className={labelCls}>Field</span>
                    <select
                      value={isPreset ? field : '__custom'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__custom') setCond(ci, { ...c, when: { ...c.when, field: '' } });
                        else if (v === 'contact.sms_opt_out') setCond(ci, { ...c, when: { field: v, op: 'eq', value: true } });
                        else setCond(ci, { ...c, when: { ...c.when, field: v, value: '' } });
                      }}
                      className={`${inputCls} min-h-[44px] sm:min-h-0`}
                    >
                      {BRANCH_FIELD_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      <option value="__custom">Custom field…</option>
                    </select>
                    {!isPreset && (
                      <input
                        type="text"
                        value={field}
                        onChange={(e) => setCond(ci, { ...c, when: { ...c.when, field: e.target.value } })}
                        placeholder="contact.… or context.…"
                        className={`${inputCls} mt-1 min-h-[44px] sm:min-h-0`}
                      />
                    )}
                  </div>
                  <div>
                    <span className={labelCls}>Comparison</span>
                    <select
                      value={op}
                      onChange={(e) => {
                        const nextOp = e.target.value;
                        const when = { ...c.when, op: nextOp };
                        if (nextOp === 'exists') delete (when as any).value;
                        else if (!('value' in when)) (when as any).value = '';
                        setCond(ci, { ...c, when });
                      }}
                      className={`${inputCls} min-h-[44px] sm:min-h-0`}
                    >
                      {BRANCH_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {op !== 'exists' && (
                    <div>
                      <span className={labelCls}>Value</span>
                      {field === 'context.last_reply.intent' ? (
                        <select
                          value={typeof c.when?.value === 'string' ? c.when.value : ''}
                          onChange={(e) => setCond(ci, { ...c, when: { ...c.when, value: e.target.value } })}
                          className={`${inputCls} min-h-[44px] sm:min-h-0`}
                        >
                          <option value="">— pick a reply —</option>
                          {INTENTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                        </select>
                      ) : field === 'contact.lifecycle_status' ? (
                        <select
                          value={typeof c.when?.value === 'string' ? c.when.value : ''}
                          onChange={(e) => setCond(ci, { ...c, when: { ...c.when, value: e.target.value } })}
                          className={`${inputCls} min-h-[44px] sm:min-h-0`}
                        >
                          <option value="">— pick a status —</option>
                          {LIFECYCLES.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      ) : field === 'contact.sms_opt_out' ? (
                        <select
                          value={c.when?.value === true ? 'true' : 'false'}
                          onChange={(e) => setCond(ci, { ...c, when: { ...c.when, value: e.target.value === 'true' } })}
                          className={`${inputCls} min-h-[44px] sm:min-h-0`}
                        >
                          <option value="true">opted out</option>
                          <option value="false">not opted out</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={c.when?.value == null ? '' : String(c.when.value)}
                          onChange={(e) => setCond(ci, { ...c, when: { ...c.when, value: e.target.value } })}
                          className={`${inputCls} min-h-[44px] sm:min-h-0`}
                        />
                      )}
                    </div>
                  )}
                  <EdgeSelect label="Go to" value={c.goto} onChange={(v) => setCond(ci, { ...c, goto: v })} nodes={nodes} exceptId={nodes[i].id} endLabel="(required — pick a step)" />
                </div>
              </div>
            );
          })}
          <button
            onClick={() => patch({ conditions: [...conds, { when: { field: 'context.last_reply.intent', op: 'eq', value: '' }, goto: null }] })}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <Plus className="h-3.5 w-3.5" /> Add condition
          </button>
          <EdgeSelect label="Otherwise" value={node.else} onChange={(v) => patch({ else: v })} nodes={nodes} exceptId={nodes[i].id} endLabel="(required — pick a step)" />
          <p className="text-[11px] text-gray-400">Conditions are checked top-to-bottom; the first match wins. Values compare exactly.</p>
        </div>
      );
    }

    if (node.type === 'create_task') {
      return (
        <div className="space-y-3">
          <div>
            <span className={labelCls}>Task title</span>
            <input
              type="text"
              value={node.title || ''}
              onChange={(e) => patch({ title: e.target.value })}
              maxLength={200}
              placeholder="Follow up"
              className={`${inputCls} min-h-[44px] sm:min-h-0`}
            />
          </div>
          <div>
            <span className={labelCls}>Notes (optional)</span>
            <textarea
              rows={2}
              value={node.description || ''}
              onChange={(e) => patch({ description: e.target.value || undefined })}
              maxLength={2000}
              className={inputCls}
            />
          </div>
          <div className="w-40">
            <span className={labelCls}>Priority</span>
            <select
              value={node.priority || 'normal'}
              onChange={(e) => patch({ priority: e.target.value })}
              className={`${inputCls} min-h-[44px] sm:min-h-0`}
            >
              {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-gray-400">The task is created unassigned — it shows on the Today page for the team.</p>
          <EdgeSelect label="Then" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
        </div>
      );
    }

    if (node.type === 'update_contact') {
      const set = node.set || {};
      const setField = (k: string, v: any) => {
        const nextSet = { ...set };
        if (v === '__unchanged') delete nextSet[k];
        else nextSet[k] = v;
        patch({ set: nextSet });
      };
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <span className={labelCls}>Pipeline status</span>
              <select
                value={'lifecycle_status' in set ? set.lifecycle_status : '__unchanged'}
                onChange={(e) => setField('lifecycle_status', e.target.value)}
                className={`${inputCls} min-h-[44px] sm:min-h-0`}
              >
                <option value="__unchanged">— don't change —</option>
                {LIFECYCLES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <span className={labelCls}>Eligibility</span>
              <select
                value={'best_eligibility' in set ? (set.best_eligibility ?? '__null') : '__unchanged'}
                onChange={(e) => setField('best_eligibility', e.target.value === '__null' ? null : e.target.value)}
                className={`${inputCls} min-h-[44px] sm:min-h-0`}
              >
                <option value="__unchanged">— don't change —</option>
                {ELIGIBILITIES.map((x) => <option key={x} value={x}>{x}</option>)}
                <option value="__null">clear</option>
              </select>
            </div>
            <div>
              <span className={labelCls}>Assign to</span>
              <select
                value={'owner_user_id' in set ? (set.owner_user_id ?? '__null') : '__unchanged'}
                onChange={(e) => setField('owner_user_id', e.target.value === '__null' ? null : e.target.value)}
                className={`${inputCls} min-h-[44px] sm:min-h-0`}
              >
                <option value="__unchanged">— don't change —</option>
                {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                <option value="__null">unassign</option>
              </select>
            </div>
          </div>
          <EdgeSelect label="Then" value={node.next} onChange={(v) => patch({ next: v })} nodes={nodes} exceptId={nodes[i].id} />
        </div>
      );
    }

    // exit
    return <p className="text-sm text-gray-500 dark:text-gray-400">Ends the flow for this patient.</p>;
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link
        to={studyId ? `/crm/settings/studies/${studyId}?tab=flows` : '/crm/settings/flows'}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" /> {studyId ? 'Study' : 'Flows'}
      </Link>

      {/* Meta header */}
      <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={meta.name}
              disabled={!isAdmin}
              onChange={(e) => { setMeta((m) => ({ ...m, name: e.target.value })); touch(); }}
              placeholder="Flow name"
              className="min-h-[44px] w-full max-w-xl rounded-lg border border-transparent bg-transparent px-2 py-1 text-xl font-semibold text-gray-900 hover:border-gray-200 focus:border-primary-500 focus:outline-none dark:text-gray-100 dark:hover:border-gray-600 sm:min-h-0"
            />
            <p className="mt-1 px-2 text-xs text-gray-400">
              {flow.version > 0 ? `Published v${flow.version}` : 'Never published'}
              {dirty ? ' · unsaved changes' : ''}
            </p>
          </div>
          {isAdmin && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                onClick={saveDraft}
                disabled={saving || publishing || !dirty}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button
                onClick={publish}
                disabled={publishing || saving || issues.length > 0}
                title={issues.length > 0 ? issues[0] : 'Snapshot this draft as the live version'}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {publishing ? 'Publishing…' : status === 'draft' ? 'Publish & activate' : 'Publish'}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <span className={labelCls}>Description</span>
            <input
              type="text"
              value={meta.description}
              disabled={!isAdmin}
              maxLength={2000}
              onChange={(e) => { setMeta((m) => ({ ...m, description: e.target.value })); touch(); }}
              placeholder="What this flow does"
              className={`${inputCls} min-h-[44px] sm:min-h-0`}
            />
          </div>
          <div>
            <span className={labelCls}>Status (applies immediately)</span>
            <select
              value={status}
              disabled={!isAdmin || statusSaving || publishing}
              onChange={(e) => changeStatus(e.target.value)}
              className={`${inputCls} min-h-[44px] sm:min-h-0`}
            >
              <option value="draft">Draft (off for new leads)</option>
              <option value="active">Active — enrolls new leads</option>
              <option value="archived">Archived (off for new leads)</option>
            </select>
            {status === 'active' && flow.version === 0 && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Active but never published — publish before it can enroll anyone.</p>
            )}
            {status !== 'active' && draftNewerThanPublish && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                The draft has edits since v{flow.version} was published — activating runs v{flow.version} until you Publish.
              </p>
            )}
          </div>
        </div>

        {notice && (
          <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            {notice}
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Steps + trigger */}
        <div className="space-y-4 lg:col-span-2">
          {/* Trigger + enrollment rules */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">When does someone enter?</h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Every <strong>new lead</strong> that matches the filters below enrolls when the flow is active.
              Blank = no filter. Filter and re-enrollment changes apply <strong>as soon as you save</strong> — they don't wait for a Publish.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {([
                ['study_slug', 'Study slugs', 'e.g. ckd-study, copd'],
                ['questionnaire_id', 'Questionnaire ids', 'e.g. a UUID from WCR'],
                ['eligibility_status', 'Lead eligibility', 'e.g. eligible'],
                ['utm_source', 'Ad source (utm_source)', 'e.g. facebook'],
              ] as const).map(([k, label, ph]) => (
                <div key={k}>
                  <span className={labelCls}>{label}</span>
                  <input
                    type="text"
                    value={trigger[k]}
                    disabled={!isAdmin}
                    onChange={(e) => { setTrigger((t) => ({ ...t, [k]: e.target.value })); touch(); }}
                    placeholder={ph}
                    className={`${inputCls} min-h-[44px] sm:min-h-0`}
                  />
                </div>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-gray-400">Comma-separate multiple values; the lead matches any of them.</p>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
                <input
                  type="checkbox"
                  checked={rules.reenroll}
                  disabled={!isAdmin}
                  onChange={(e) => { setRules((r) => ({ ...r, reenroll: e.target.checked })); touch(); }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Allow re-enrollment (default: once per patient, ever)
              </label>
              {rules.reenroll && (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  but not within
                  <input
                    type="number"
                    min={1}
                    value={rules.dedupe}
                    disabled={!isAdmin}
                    onChange={(e) => { setRules((r) => ({ ...r, dedupe: e.target.value })); touch(); }}
                    className={`${inputCls} w-20 min-h-[44px] sm:min-h-0`}
                  />
                  hours of the last run
                </label>
              )}
            </div>
          </div>

          {/* Steps */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Steps <span className="text-sm font-normal text-gray-500">({nodes.length})</span>
              </h3>
              <div className="flex items-center gap-2">
                {nodes.length > 1 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Start at</span>
                    <select
                      value={effectiveEntry || ''}
                      disabled={!isAdmin}
                      onChange={(e) => { setEntry(e.target.value || null); touch(); }}
                      className={`${inputCls} w-auto min-h-[44px] sm:min-h-0`}
                      aria-label="Starting step"
                    >
                      {nodes.map((n, i) => <option key={n.id} value={n.id}>{nodeLabel(i, n.node)}</option>)}
                    </select>
                  </div>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setAddOpen((v) => !v)}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add step
                  </button>
                )}
              </div>
            </div>

            {addOpen && (
              <div className="mb-3 grid grid-cols-1 gap-1.5 rounded-lg border border-primary-200 bg-primary-50/50 p-2 dark:border-primary-800 dark:bg-primary-900/10 sm:grid-cols-3">
                {NODE_TYPE_META.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => addNode(t.value)}
                    className="min-h-[44px] rounded-lg px-2.5 py-2 text-left transition-colors duration-100 hover:bg-white dark:hover:bg-gray-700/60 sm:min-h-0"
                  >
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t.label}</span>
                    <span className="block text-[11px] leading-tight text-gray-500 dark:text-gray-400">{t.blurb}</span>
                  </button>
                ))}
              </div>
            )}

            {nodes.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No steps yet — add one. A typical flow: Send the options menu → Wait for a reply → Branch on the answer.
              </p>
            ) : (
              <div className="space-y-2">
                {nodes.map((n, i) => (
                  <div key={n.id} className="rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => setOpenIdx(openIdx === i ? null : i)}
                        className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 text-left sm:min-h-0"
                      >
                        {openIdx === i
                          ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                          : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {nodeLabel(i, n.node)}
                        </span>
                        {effectiveEntry === n.id && (
                          <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                            start
                          </span>
                        )}
                        {(n.node.type === 'wait' || n.node.type === 'wait_for_reply') && (
                          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                            {n.node.type === 'wait' && n.node.until_business_hours ? 'business hours' : durationLabel(n.node.type === 'wait' ? n.node.duration : n.node.timeout)}
                          </span>
                        )}
                      </button>
                      {isAdmin && (
                        <button onClick={() => remove(i)} className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded p-1 text-gray-400 hover:text-red-600 sm:min-h-0 sm:min-w-0" title="Remove step" aria-label="Remove step">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {openIdx === i && (
                      <div className="border-t border-gray-100 px-3 py-3 dark:border-gray-700">
                        {isAdmin ? renderEditor(i, n.node) : (
                          <p className="text-xs text-gray-400">Read-only — CRM admin required to edit.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Flow map + checks + versions */}
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Checks</h3>
            {issues.length === 0 ? (
              <p className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" /> Ready to publish.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {issues.map((iss, k) => (
                  <li key={k} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{iss}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Flow map</h3>
            {reachable.length === 0 ? (
              <p className="text-xs text-gray-400">Add a step to see the path.</p>
            ) : (
              <div className="space-y-2">
                {reachable.map((nid) => {
                  const i = idxOf(nid);
                  const node = nodes[i].node;
                  const edgeLines: { label: string; to: string | null | undefined }[] = [];
                  if (node.type === 'branch') {
                    (node.conditions || []).forEach((c, ci) => edgeLines.push({ label: `if ${ci + 1}`, to: c.goto }));
                    edgeLines.push({ label: 'otherwise', to: node.else });
                  } else if (node.type === 'wait_for_reply') {
                    edgeLines.push({ label: 'reply', to: node.next });
                    edgeLines.push({ label: 'silence', to: node.on_timeout });
                  } else if (node.type !== 'exit') {
                    edgeLines.push({ label: 'then', to: node.next });
                  }
                  return (
                    <div key={nid} className="text-xs">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {nodeLabel(i, node)}
                        {effectiveEntry === nid && <span className="ml-1.5 text-primary-600 dark:text-primary-400">(start)</span>}
                      </p>
                      {edgeLines.map((e, k) => (
                        <p key={k} className="pl-3 text-gray-500 dark:text-gray-400">
                          {e.label} → {typeof e.to === 'string' && e.to ? (idxOf(e.to) >= 0 ? nodeLabel(idxOf(e.to), nodes[idxOf(e.to)].node) : '⚠ deleted step') : 'end'}
                        </p>
                      ))}
                    </div>
                  );
                })}
                {nodes.filter((n) => !reachable.includes(n.id)).length > 0 && (
                  <p className="border-t border-gray-100 pt-2 text-[11px] text-amber-600 dark:border-gray-700 dark:text-amber-400">
                    Not reachable from the start: {nodes.filter((n) => !reachable.includes(n.id)).map((n) => nodeLabel(idxOf(n.id), n.node)).join('; ')}
                  </p>
                )}
              </div>
            )}
          </div>

          {versions.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Version history</h3>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      v{v.version}
                      {v.version === flow.version && (
                        <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">live</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(v.published_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Patients already in the flow finish on the version they started — new enrollments use the latest.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
